import React, { useState, useEffect, useCallback, useContext } from "react";
import {
  Box, Typography, Select, MenuItem, CircularProgress, IconButton,
  Table, TableHead, TableBody, TableRow, TableCell, Switch, Tooltip,
  Menu, FormControlLabel, Checkbox, Button, Chip
} from "@material-ui/core";
import ViewColumnIcon from "@material-ui/icons/ViewColumn";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import RefreshIcon from "@material-ui/icons/Refresh";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import api from "../../../services/api";
import toastError from "../../../errors/toastError";
import { AuthContext } from "../../../context/Auth/AuthContext";
import { usePlanPermissions } from "../../../context/PlanPermissionsContext";

// Todas as colunas existem sempre; o usuario escolhe quais aparecem.
// A escolha fica gravada no proprio navegador, por empresa.
const COLUNAS = [
  { id: "orcamento",            rotulo: "Orçamento",              tipo: "dinheiro" },
  { id: "valorUsado",           rotulo: "Valor usado",            tipo: "dinheiro" },
  { id: "resultado",            rotulo: "Resultado",              tipo: "numero" },
  { id: "custoPorResultado",    rotulo: "Custo por resultado",    tipo: "dinheiro" },
  { id: "compras",              rotulo: "Compras",                tipo: "numero" },
  { id: "custoPorCompra",       rotulo: "CPA",                    tipo: "dinheiro" },
  { id: "valorConversaoCompra", rotulo: "Valor conv. compra",     tipo: "dinheiro" },
  { id: "valorTotalConversao",  rotulo: "Valor total conv.",      tipo: "dinheiro" },
  { id: "checkouts",            rotulo: "IC",                     tipo: "numero" },
  { id: "custoPorCheckout",     rotulo: "Custo por IC",           tipo: "dinheiro" },
  { id: "conversas",            rotulo: "Conversas iniciadas",    tipo: "numero" },
  { id: "custoPorConversa",     rotulo: "Custo por conversa",     tipo: "dinheiro" },
  { id: "cpcLink",              rotulo: "CPC do link",            tipo: "dinheiro" },
  { id: "ctrLink",              rotulo: "CTR do link",            tipo: "percentual" },
  { id: "cpm",                  rotulo: "CPM",                    tipo: "dinheiro" },
  { id: "alcance",              rotulo: "Alcance",                tipo: "numero" },
  { id: "impressoes",           rotulo: "Impressões",             tipo: "numero" },
  { id: "cliquesNoLink",        rotulo: "Cliques no link",        tipo: "numero" },
  { id: "visitasNaPagina",      rotulo: "Visitas na página",      tipo: "numero" },
  { id: "custoPorVisita",       rotulo: "Custo por visita",       tipo: "dinheiro" },
];

const PADRAO = [
  "orcamento", "valorUsado", "resultado", "custoPorResultado",
  "compras", "custoPorCompra", "cpcLink", "ctrLink", "impressoes"
];

// Colunas de custo e taxa sao medias: somar daria numero errado.
const NAO_SOMA = (id) =>
  id.startsWith("custoPor") || id === "cpcLink" || id === "cpm" || id === "ctrLink";

const PERIODOS = [
  { v: "today",        t: "Hoje" },
  { v: "yesterday",    t: "Ontem" },
  { v: "last_7d",      t: "Últimos 7 dias" },
  { v: "last_14d",     t: "Últimos 14 dias" },
  { v: "last_30d",     t: "Últimos 30 dias" },
  { v: "this_month",   t: "Este mês" },
  { v: "last_month",   t: "Mês passado" },
];

const dinheiro = (v) =>
  `R$ ${Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const numero = (v) => Number(v || 0).toLocaleString("pt-BR");
const percentual = (v) => `${Number(v || 0).toFixed(2)}%`;

const formatar = (valor, tipo) =>
  tipo === "dinheiro" ? dinheiro(valor) : tipo === "percentual" ? percentual(valor) : numero(valor);

export default function MetaAds() {
  const { user } = useContext(AuthContext);
  const history = useHistory();
  const { metaAds, loading: planLoading } = usePlanPermissions();

  const chaveConta = `metaAds_conta_${user?.companyId}`;
  const chaveColunas = `metaAds_colunas_${user?.companyId}`;

  const [contas, setContas] = useState([]);
  const [conta, setConta] = useState(localStorage.getItem(chaveConta) || "");
  const [periodo, setPeriodo] = useState("today");
  const [campanhas, setCampanhas] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState("");
  const [menuColunas, setMenuColunas] = useState(null);
  const [soAtivas, setSoAtivas] = useState(true);
  // Guarda os filhos ja carregados: { [idDoPai]: [linhas] }
  const [filhos, setFilhos] = useState({});
  const [abertos, setAbertos] = useState({});
  const [carregandoFilho, setCarregandoFilho] = useState({});
  const [visiveis, setVisiveis] = useState(() => {
    try {
      const salvo = JSON.parse(localStorage.getItem(chaveColunas) || "null");
      return Array.isArray(salvo) && salvo.length ? salvo : PADRAO;
    } catch (e) {
      return PADRAO;
    }
  });

  useEffect(() => {
    if (!planLoading && !metaAds) {
      toast.warn("Seu plano não inclui este recurso.");
      history.push("/");
    }
  }, [planLoading, metaAds, history]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/ads/meta/contas");
        setContas(data || []);
        if (!conta && data?.length) {
          const primeiraAtiva = data.find((c) => c.ativa) || data[0];
          setConta(primeiraAtiva.id);
          localStorage.setItem(chaveConta, primeiraAtiva.id);
        }
      } catch (e) {
        setErro(e?.response?.data?.error || "Não foi possível listar suas contas de anúncio.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buscar = useCallback(async () => {
    if (!conta) return;
    setCarregando(true);
    setErro("");
    try {
      const { data } = await api.get("/ads/meta/campanhas", { params: { conta, periodo } });
      setCampanhas(data || []);
    } catch (e) {
      setErro(e?.response?.data?.error || "Não foi possível carregar as campanhas.");
      setCampanhas([]);
    }
    setCarregando(false);
  }, [conta, periodo]);

  useEffect(() => { buscar(); }, [buscar]);

  const alternar = async (campanha) => {
    const novo = !campanha.ativa;
    // Muda na tela antes da resposta: pausar anúncio é urgente, esperar
    // a Meta responder para só então mudar a chave passa sensação de travado.
    const marcar = (valor) => {
      setCampanhas((cs) => cs.map((c) => (c.id === campanha.id ? { ...c, ativa: valor } : c)));
      setFilhos((f) => {
        const novoMapa = { ...f };
        Object.keys(novoMapa).forEach((k) => {
          novoMapa[k] = novoMapa[k].map((x) => (x.id === campanha.id ? { ...x, ativa: valor } : x));
        });
        return novoMapa;
      });
    };

    marcar(novo);
    const nomeDoNivel =
      campanha.nivel === "conjunto" ? "Conjunto" : campanha.nivel === "anuncio" ? "Anúncio" : "Campanha";
    try {
      await api.put("/ads/meta/campanha", { campanhaId: campanha.id, ativar: novo });
      toast.success(`${nomeDoNivel} ${novo ? "ativado" : "pausado"}.`);
    } catch (e) {
      marcar(!novo);
      toast.error(e?.response?.data?.error || "Não foi possível alterar.");
    }
  };

  const trocarColuna = (id) => {
    setVisiveis((atual) => {
      const novo = atual.includes(id) ? atual.filter((x) => x !== id) : [...atual, id];
      localStorage.setItem(chaveColunas, JSON.stringify(novo));
      return novo;
    });
  };

  // Abre um nivel puxando os filhos so quando o usuario pede — carregar tudo
  // de uma vez faria dezenas de chamadas e estouraria o limite da Meta.
  const alternarAberto = async (linha, objetivo) => {
    const jaAberto = !!abertos[linha.id];
    setAbertos((a) => ({ ...a, [linha.id]: !jaAberto }));
    if (jaAberto || filhos[linha.id]) return;

    setCarregandoFilho((c) => ({ ...c, [linha.id]: true }));
    try {
      const rota = linha.nivel === "campanha" ? "/ads/meta/conjuntos" : "/ads/meta/anuncios";
      const chave = linha.nivel === "campanha" ? "campanha" : "conjunto";
      const { data } = await api.get(rota, {
        params: { [chave]: linha.id, periodo, objetivo }
      });
      setFilhos((f) => ({ ...f, [linha.id]: data || [] }));
    } catch (e) {
      toast.error(e?.response?.data?.error || "Não foi possível carregar.");
      setAbertos((a) => ({ ...a, [linha.id]: false }));
    }
    setCarregandoFilho((c) => ({ ...c, [linha.id]: false }));
  };

  const moverColuna = (id, direcao) => {
    setVisiveis((atual) => {
      const i = atual.indexOf(id);
      const j = i + direcao;
      if (i < 0 || j < 0 || j >= atual.length) return atual;
      const novo = [...atual];
      [novo[i], novo[j]] = [novo[j], novo[i]];
      localStorage.setItem(chaveColunas, JSON.stringify(novo));
      return novo;
    });
  };

  // A ordem que vale e a que o usuario montou, nao a ordem da lista fixa.
  const colunasAtivas = visiveis
    .map((id) => COLUNAS.find((c) => c.id === id))
    .filter(Boolean);

  const campanhasVisiveis = soAtivas ? campanhas.filter((c) => c.ativa) : campanhas;

  // Bordas finas em toda celula: le como planilha e o olho acompanha a linha
  // sem se perder quando ha muitas colunas.
  const celula = {
    borderRight: "1px solid #eef1f4",
    borderBottom: "1px solid #eef1f4",
    fontSize: 13,
    padding: "6px 10px",
    whiteSpace: "nowrap"
  };
  const celulaCabecalho = { ...celula, fontWeight: 700, fontSize: 12.5, color: "#3a4a5a" };

  const linha = (item, nivel, objetivo) => {
    const podeAbrir = nivel < 2;
    const aberto = !!abertos[item.id];
    return (
      <TableRow key={item.id} hover style={nivel > 0 ? { background: "#fbfcfd" } : undefined}>
        <TableCell style={{ ...celula, minWidth: 260 }}>
          <Box style={{ display: "flex", alignItems: "center", paddingLeft: nivel * 18 }}>
            {podeAbrir ? (
              <IconButton
                size="small"
                onClick={() => alternarAberto(item, objetivo)}
                style={{ marginRight: 2 }}
              >
                {carregandoFilho[item.id] ? (
                  <CircularProgress size={13} />
                ) : aberto ? (
                  <ExpandMoreIcon style={{ fontSize: 17 }} />
                ) : (
                  <ChevronRightIcon style={{ fontSize: 17 }} />
                )}
              </IconButton>
            ) : (
              <span style={{ display: "inline-block", width: 26 }} />
            )}
            <Box style={{ minWidth: 0 }}>
              <div style={{ fontWeight: nivel === 0 ? 600 : 500 }}>{item.nome}</div>
              <div style={{ fontSize: 11, color: "#8a97a3" }}>
                {nivel === 0 ? item.objetivo : nivel === 1 ? "conjunto" : "anúncio"}
              </div>
            </Box>
          </Box>
        </TableCell>
        <TableCell style={{ ...celula, textAlign: "center" }}>
          <Switch size="small" color="primary" checked={!!item.ativa} onChange={() => alternar(item)} />
        </TableCell>
        {colunasAtivas.map((c) => (
          <TableCell key={c.id} align="right" style={celula}>
            {formatar(item[c.id], c.tipo)}
          </TableCell>
        ))}
      </TableRow>
    );
  };

  const totais = colunasAtivas.reduce((acc, col) => {
    if (NAO_SOMA(col.id)) {
      acc[col.id] = null;
    } else {
      acc[col.id] = campanhasVisiveis.reduce((s, c) => s + Number(c[col.id] || 0), 0);
    }
    return acc;
  }, {});

  return (
    <Box style={{ padding: 20, height: "100%", overflow: "auto" }}>
      <Box style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
        <Typography style={{ fontWeight: 700, fontSize: 20, marginRight: 8 }}>
          Meta Ads
        </Typography>

        <Select
          value={conta}
          onChange={(e) => {
            const v = e.target.value;
            setConta(v);
            localStorage.setItem(chaveConta, v);
          }}
          variant="outlined"
          style={{ minWidth: 280, height: 38 }}
          displayEmpty
        >
          {contas.length === 0 && <MenuItem value="">Nenhuma conta encontrada</MenuItem>}
          {contas.map((c) => (
            <MenuItem key={c.id} value={c.id}>
              {c.nome}
              {!c.ativa && (
                <Chip size="small" label="inativa" style={{ marginLeft: 8, height: 18, fontSize: 10 }} />
              )}
            </MenuItem>
          ))}
        </Select>

        <Select
          value={periodo}
          onChange={(e) => setPeriodo(e.target.value)}
          variant="outlined"
          style={{ minWidth: 170, height: 38 }}
        >
          {PERIODOS.map((p) => (
            <MenuItem key={p.v} value={p.v}>{p.t}</MenuItem>
          ))}
        </Select>

        <FormControlLabel
          control={
            <Switch
              size="small"
              color="primary"
              checked={soAtivas}
              onChange={(e) => setSoAtivas(e.target.checked)}
            />
          }
          label={<span style={{ fontSize: 13 }}>Só ativas</span>}
        />

        <Tooltip title="Escolher colunas">
          <IconButton onClick={(e) => setMenuColunas(e.currentTarget)}>
            <ViewColumnIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title="Atualizar">
          <IconButton onClick={buscar}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>

        <Menu anchorEl={menuColunas} open={!!menuColunas} onClose={() => setMenuColunas(null)}>
          <Box style={{ padding: "4px 12px", maxHeight: 380, overflow: "auto" }}>
            <Typography style={{ fontSize: 11.5, color: "#667781", padding: "4px 0 6px" }}>
              A ordem abaixo é a ordem das colunas na tabela.
            </Typography>
            {visiveis.map((id, i) => {
              const c = COLUNAS.find((x) => x.id === id);
              if (!c) return null;
              return (
                <Box key={id} style={{ display: "flex", alignItems: "center" }}>
                  <Checkbox size="small" color="primary" checked onChange={() => trocarColuna(id)} />
                  <span style={{ fontSize: 13, flex: 1 }}>{c.rotulo}</span>
                  <IconButton size="small" disabled={i === 0} onClick={() => moverColuna(id, -1)}>
                    <ArrowUpwardIcon style={{ fontSize: 15 }} />
                  </IconButton>
                  <IconButton size="small" disabled={i === visiveis.length - 1} onClick={() => moverColuna(id, 1)}>
                    <ArrowDownwardIcon style={{ fontSize: 15 }} />
                  </IconButton>
                </Box>
              );
            })}
            <Box style={{ borderTop: "1px solid #e5e7eb", margin: "6px 0" }} />
            {COLUNAS.filter((c) => !visiveis.includes(c.id)).map((c) => (
              <Box key={c.id} style={{ display: "flex", alignItems: "center" }}>
                <Checkbox size="small" color="primary" checked={false} onChange={() => trocarColuna(c.id)} />
                <span style={{ fontSize: 13, color: "#667781" }}>{c.rotulo}</span>
              </Box>
            ))}
            <Button size="small" onClick={() => { setVisiveis(PADRAO); localStorage.setItem(chaveColunas, JSON.stringify(PADRAO)); }}>
              Restaurar padrão
            </Button>
          </Box>
        </Menu>
      </Box>

      {erro && (
        <Box style={{ background: "#fff4e5", border: "1px solid #ffb74d", borderRadius: 8, padding: 12, marginBottom: 14 }}>
          <Typography style={{ fontSize: 13.5 }}>{erro}</Typography>
        </Box>
      )}

      {carregando ? (
        <Box style={{ display: "flex", justifyContent: "center", padding: 40 }}>
          <CircularProgress />
        </Box>
      ) : campanhasVisiveis.length === 0 && !erro ? (
        <Typography style={{ color: "#667781", fontSize: 14 }}>
          {soAtivas
            ? "Nenhuma campanha ativa nesta conta para o período."
            : "Nenhuma campanha nesta conta para o período escolhido."}
        </Typography>
      ) : (
        <Box style={{ overflowX: "auto", border: "1px solid #e3e6ea", borderRadius: 8 }}>
          <Table size="small" style={{ borderCollapse: "collapse" }}>
            <TableHead>
              <TableRow style={{ background: "#f6f8fa" }}>
                <TableCell style={celulaCabecalho}>Campanha</TableCell>
                <TableCell style={{ ...celulaCabecalho, textAlign: "center" }}>Ativa</TableCell>
                {colunasAtivas.map((c) => (
                  <TableCell key={c.id} align="right" style={celulaCabecalho}>
                    {c.rotulo}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {campanhasVisiveis.map((camp) => (
                <React.Fragment key={camp.id}>
                  {linha(camp, 0, camp.objetivo)}

                  {abertos[camp.id] &&
                    (filhos[camp.id] || [])
                      .filter((cj) => (soAtivas ? cj.ativa : true))
                      .map((cj) => (
                        <React.Fragment key={cj.id}>
                          {linha(cj, 1, camp.objetivo)}
                          {abertos[cj.id] &&
                            (filhos[cj.id] || [])
                              .filter((an) => (soAtivas ? an.ativa : true))
                              .map((an) => linha(an, 2, camp.objetivo))}
                        </React.Fragment>
                      ))}
                </React.Fragment>
              ))}

              <TableRow style={{ background: "#f6f8fa" }}>
                <TableCell style={{ ...celula, fontWeight: 700 }}>Total</TableCell>
                <TableCell style={celula} />
                {colunasAtivas.map((c) => (
                  <TableCell key={c.id} align="right" style={{ ...celula, fontWeight: 700 }}>
                    {totais[c.id] === null ? "—" : formatar(totais[c.id], c.tipo)}
                  </TableCell>
                ))}
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
}
